import { nanoid, type Store } from '@blocksuite/affine/store';
import { useEffect, useState } from 'react';
import type * as Y from 'yjs';

import * as styles from './object-annotation.css';
import {
  AnnotationPicker,
  rememberVocabulary,
} from './object-annotation-picker';
import type {
  AnnotationProperty,
  PropertyType,
} from './object-annotation-store';

const TYPES: PropertyType[] = ['text', 'number', 'boolean', 'date', 'url'];

export function ObjectAnnotationMetadata({
  parent,
  annotation,
  readonly,
}: {
  parent: Store;
  annotation: Y.Map<unknown>;
  readonly: boolean;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [properties, setProperties] = useState<[string, AnnotationProperty][]>(
    []
  );
  const [addingProperty, setAddingProperty] = useState(false);
  const [propertyName, setPropertyName] = useState('');
  const [propertyType, setPropertyType] = useState<PropertyType>('text');
  const [error, setError] = useState('');
  const disabled = readonly || parent.readonly;
  const tagMap = annotation.get('tags') as Y.Map<boolean> | undefined;
  const propertyMap = annotation.get('properties') as
    | Y.Map<AnnotationProperty>
    | undefined;

  useEffect(() => {
    const updateTags = () => setTags(tagMap ? [...tagMap.keys()] : []);
    const updateProperties = () =>
      setProperties(propertyMap ? [...propertyMap.entries()] : []);
    updateTags();
    updateProperties();
    tagMap?.observe(updateTags);
    propertyMap?.observe(updateProperties);
    return () => {
      tagMap?.unobserve(updateTags);
      propertyMap?.unobserve(updateProperties);
    };
  }, [tagMap, propertyMap]);

  const edit = (fn: () => void) => {
    if (disabled) return;
    parent.captureSync();
    parent.transact(fn);
  };
  const addTag = (name: string) => {
    if (!name || !tagMap || disabled) return;
    if (
      tags.some(value => value.toLocaleLowerCase() === name.toLocaleLowerCase())
    ) {
      setError('This tag is already attached.');
      return;
    }
    edit(() => {
      tagMap.set(name, true);
      rememberVocabulary(parent, 'tags', name, '');
    });
    setError('');
  };
  const addProperty = () => {
    const name = propertyName.trim();
    if (!name || !propertyMap || disabled) return;
    if (
      properties.some(
        ([, p]) => p.name.toLocaleLowerCase() === name.toLocaleLowerCase()
      )
    ) {
      setError('This property name already exists.');
      return;
    }
    edit(() =>
      propertyMap.set(nanoid(), {
        name,
        type: propertyType,
        value:
          propertyType === 'boolean'
            ? false
            : propertyType === 'number'
              ? null
              : '',
      })
    );
    setPropertyName('');
    setAddingProperty(false);
    setError('');
  };

  return (
    <>
      <section className={styles.metadataTags}>
        <div className={styles.sectionTitle}>Tags</div>
        <div className={styles.labelRow}>
          {tags.map(name => (
            <span
              key={name}
              className={styles.tag}
              data-testid="object-annotation-tag"
            >
              #{name}
              {!disabled && (
                <button
                  aria-label={'Remove tag ' + name}
                  onClick={() => edit(() => tagMap?.delete(name))}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!disabled && (
            <AnnotationPicker
              parent={parent}
              kind="tags"
              attached={tags}
              onSelect={addTag}
            />
          )}
        </div>
      </section>
      <section className={styles.metadataSection}>
        <div className={styles.metadataTitle}>
          <span className={styles.sectionTitle}>Properties</span>
          {!disabled && (
            <button
              className={styles.addButton}
              aria-label="Add property"
              onClick={() => setAddingProperty(!addingProperty)}
            >
              +
            </button>
          )}
        </div>
        <div className={styles.propertyList}>
          {properties.map(([id, property]) => (
            <div
              className={styles.propertyRow}
              key={id}
              data-testid="object-annotation-property"
              data-property-name={property.name}
              data-property-type={property.type}
            >
              <div className={styles.propertyHeading}>
                <strong title={property.name}>{property.name}</strong>
                <span>
                  {property.type === 'boolean' ? 'checkbox' : property.type}
                </span>
                {!disabled && (
                  <button
                    className={styles.iconButton}
                    aria-label={'Remove property ' + property.name}
                    onClick={() => edit(() => propertyMap?.delete(id))}
                  >
                    ×
                  </button>
                )}
              </div>
              {property.type === 'boolean' ? (
                <button
                  type="button"
                  role="checkbox"
                  className={styles.checkbox}
                  aria-label={'Property ' + property.name}
                  disabled={disabled}
                  aria-checked={property.value === true}
                  onClick={() =>
                    edit(() =>
                      propertyMap?.set(id, {
                        ...property,
                        value: property.value !== true,
                      })
                    )
                  }
                >
                  {property.value === true ? '✓' : ''}
                </button>
              ) : (
                <input
                  type={property.type === 'text' ? 'text' : property.type}
                  step={property.type === 'number' ? 'any' : undefined}
                  aria-label={'Property ' + property.name}
                  disabled={disabled}
                  placeholder={property.type === 'url' ? 'https://…' : 'Value'}
                  value={
                    typeof property.value === 'boolean'
                      ? ''
                      : (property.value ?? '')
                  }
                  onChange={e => {
                    const value =
                      property.type === 'number'
                        ? e.target.value === '' ||
                          !Number.isFinite(e.target.valueAsNumber)
                          ? null
                          : e.target.valueAsNumber
                        : e.target.value;
                    edit(() => propertyMap?.set(id, { ...property, value }));
                  }}
                />
              )}
            </div>
          ))}
        </div>
        {addingProperty && !disabled && (
          <form
            className={styles.labelForm}
            onSubmit={e => {
              e.preventDefault();
              addProperty();
            }}
          >
            <input
              autoFocus
              aria-label="Property name"
              placeholder="Property name"
              maxLength={80}
              value={propertyName}
              onChange={e => setPropertyName(e.target.value)}
            />
            <select
              aria-label="Property type"
              value={propertyType}
              onChange={e => setPropertyType(e.target.value as PropertyType)}
            >
              {TYPES.map(type => (
                <option key={type} value={type}>
                  {type === 'boolean'
                    ? 'Checkbox'
                    : type === 'url'
                      ? 'URL'
                      : type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={!propertyName.trim()}
            >
              Add property
            </button>
          </form>
        )}
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
      </section>
    </>
  );
}
